import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { InternalOrderImportModel } from 'src/app/models/imports/internal-order-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InternalOrderPrintModel } from 'src/app/models/shopping/internal-order-print.model';
import { InternalOrderModel } from 'src/app/models/shopping/internal-order.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InternalOrderService {

  constructor(private _httpClient: HttpClient) { }

  annulInternalOrder(internalOrderContract: InternalOrderModel): Observable<InternalOrderModel> {
    return this._httpClient.put<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.AnnulInternalOrder}`,
      internalOrderContract);
  }

  createInternalOrder(internalOrderContract: InternalOrderModel): Observable<InternalOrderModel> {
    return this._httpClient.post<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.CreateInternalOrder}`,
      internalOrderContract);
  }

  deleteInternalOrder(internalOrderId: number): Observable<InternalOrderModel> {
    return this._httpClient.delete<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.DeleteInternalOrder}/${internalOrderId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getAllInternalOrder(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetAllInternalOrders}`,
      filters, { params: params }
    );
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetColumnsForGeneralConsultation}`);
  }

  getFirstInternalOrderByKeysAsync(branchId: number): Observable<InternalOrderModel> {
    let params = new HttpParams()
    params = params.set('branchId', branchId.toString());
    return this._httpClient.get<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetFirstInternalOrderByKeys}`, { params: params });
  }

  getInternalOrderById(internalOrderId: number): Observable<InternalOrderModel> {
    return this._httpClient.get<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetInternalOrderById}/${internalOrderId}`);
  }

  getInternalOrderByKeysAsync(branchId: number, consecutive: number): Observable<InternalOrderModel> {
    let params = new HttpParams()
    params = params.set('branchId', branchId.toString());
    params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetInternalOrderByKeys}`, { params: params });
  }

  getLastInternalOrderByKeysAsync(branchId: number): Observable<InternalOrderModel> {
    let params = new HttpParams()
    params = params.set('branchId', branchId.toString());
    return this._httpClient.get<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetLastInternalOrderByKeys}`, { params: params });
  }

  getNextInternalOrderByKeysAsync(branchId: number, consecutive: number | null): Observable<InternalOrderModel> {
    let params = new HttpParams()
    params = params.set('branchId', branchId.toString());
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetNextInternalOrderByKeys}`, { params: params });
  }

  getPermission(permission: number): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetPermission}/${permission}`);
  }

  getPrintInternalOrder(internalOrderId: number): Observable<InternalOrderPrintModel> {
    return this._httpClient.get<InternalOrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetPrintInternalOrder}/${internalOrderId}`);
  }

  getEmptyPrintInternalOrder(): Observable<InternalOrderPrintModel> {
    return this._httpClient.get<InternalOrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetEmptyPrintInternalOrder}`);
  }

  getPrintInternalOrderByFormat(branchId: number): Observable<InternalOrderPrintModel> {
    return this._httpClient.get<InternalOrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetPrintInternalOrderByFormat}/${branchId}`);
  }

  getPreviusInternalOrderByKeysAsync(branchId: number, consecutive: number | null): Observable<InternalOrderModel> {
    let params = new HttpParams()
    params = params.set('branchId', branchId.toString());
    if (consecutive !== null)
      params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.GetPreviusInternalOrderByKeys}`, { params: params });
  }

  importInternalOrder(internalOrdersImport: Array<InternalOrderImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.ImportInteralOrder}`,
      internalOrdersImport);
  }

  setInternalOrderState(internalOrderId: number): Observable<InternalOrderModel> {
    return this._httpClient.put<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.SetInternalOrderState}/${internalOrderId}`,
      null);
  }

  updateInternalOrder(internalOrderContract: InternalOrderModel): Observable<InternalOrderModel> {
    return this._httpClient.put<InternalOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.internalOrder.UpdateInternalOrder}`,
      internalOrderContract);
  }
}
